import React, { useEffect, useState } from 'react';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { SocialIconRow } from './SocialIcons';

const LINKS = [
  { label: 'Home',         href: '#home',         id: 'home' },
  { label: 'About',        href: '#about',        id: 'about' },
  { label: 'Training',     href: '#training',     id: 'training' },
  { label: 'Clients',      href: '#clients',      id: 'clients' },
  { label: 'Testimonials', href: '#testimonials', id: 'testimonials' },
  { label: 'Hall of Fame', href: '#blog',         id: 'blog' },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [active,   setActive]   = useState('home');
  const [open,     setOpen]     = useState(false);

  useEffect(() => {
    const ids = LINKS.map(l => l.id);

    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 50);

      // active section: last section whose top is within the top 40% of viewport
      const trigger = y + window.innerHeight * 0.4;
      for (let i = ids.length - 1; i >= 0; i--) {
        const el = document.getElementById(ids[i]);
        if (el && el.offsetTop <= trigger) { setActive(ids[i]); return; }
      }
      setActive('home');
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleClick = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      {/* ── Top bar ── */}
      <header
        data-testid="main-nav"
        style={{
          position: 'fixed',
          top: 0, left: 0, right: 0,
          zIndex: 50,
          padding: scrolled ? '12px 0' : '20px 0',
          background:      scrolled ? 'rgba(10,10,15,0.85)' : 'transparent',
          backdropFilter:  scrolled ? 'blur(16px)'          : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(16px)'     : 'none',
          borderBottom:    `1px solid ${scrolled ? 'rgba(0,245,212,0.15)' : 'transparent'}`,
          transition: 'padding 0.3s ease, background 0.3s ease, border-color 0.3s ease',
        }}
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-10 flex items-center">

          {/* Logo — hard left, never moves */}
          <a
            href="#home"
            onClick={(e) => handleClick(e, '#home')}
            data-testid="nav-logo"
            className="font-display font-black text-xl tracking-tight text-white hover:text-teal transition-colors shrink-0"
          >
            PARTH<span className="text-teal">.</span>SHARMA
          </a>

          {/* CENTER — nav links fade in on scroll */}
          <div className="flex-1 hidden lg:flex items-center justify-center overflow-hidden">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '28px',
                opacity:   scrolled ? 1 : 0,
                transform: scrolled ? 'translateY(0)' : 'translateY(-6px)',
                transition: 'opacity 0.3s ease, transform 0.3s ease',
                pointerEvents: scrolled ? 'auto' : 'none',
              }}
            >
              {LINKS.map(({ label, href, id }) => {
                const isActive = active === id;
                return (
                  <a
                    key={id}
                    href={href}
                    onClick={(e) => handleClick(e, href)}
                    data-testid={`nav-link-${id}`}
                    className={`font-mono text-[12px] uppercase tracking-[0.08em] whitespace-nowrap transition-colors
                      flex flex-col items-center gap-[3px]
                      ${isActive ? 'text-teal' : 'text-white/55 hover:text-teal'}`}
                  >
                    {label}
                    <span
                      className="w-1 h-1 rounded-full bg-teal block"
                      style={{ opacity: isActive ? 1 : 0, transition: 'opacity 0.2s ease' }}
                    />
                  </a>
                );
              })}
            </div>
          </div>

          {/* RIGHT — social icons (state 1 only) + CTA (always) */}
          <div className="hidden lg:flex items-center">

            {/* Social icons + divider — collapse + fade on scroll */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                maxWidth: scrolled ? '0px' : '200px',
                overflow: 'hidden',
                opacity: scrolled ? 0 : 1,
                transition: 'max-width 0.3s ease, opacity 0.2s ease',
                flexShrink: 0,
              }}
            >
              {/* Extra inner wrapper keeps icons from wrapping mid-collapse */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                <SocialIconRow size={20} />
              </div>
              <div style={{
                width: '1px', height: '20px',
                background: 'rgba(255,255,255,0.2)',
                margin: '0 16px',
                flexShrink: 0,
              }} />
            </div>

            {/* CTA — hard right, never moves */}
            <a
              href="#contact"
              onClick={(e) => handleClick(e, '#contact')}
              data-testid="nav-cta-book"
              className="group inline-flex items-center gap-1 px-4 py-2 bg-teal text-[#0a0a0f] font-mono text-xs uppercase tracking-[0.15em] font-semibold rounded-full hover:bg-white transition-colors shrink-0"
            >
              Book a Call
              <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="lg:hidden text-white ml-auto"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </header>

      {/* ── Mobile full-screen overlay ── */}
      {open && (
        <div className="fixed inset-0 z-40 bg-[#0a0a0f]/95 backdrop-blur-xl lg:hidden flex flex-col pt-24 px-6">
          <ul className="flex flex-col gap-6">
            {LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={(e) => handleClick(e, l.href)}
                  className="font-display text-3xl transition-colors"
                  style={{ color: active === l.id ? '#00F5D4' : 'white' }}
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
          <div className="mt-auto pb-10 flex flex-col gap-6">
            <a
              href="#contact"
              onClick={(e) => handleClick(e, '#contact')}
              className="inline-flex items-center justify-center gap-2 w-full py-4 bg-teal text-[#0a0a0f] font-mono text-sm uppercase tracking-[0.15em] font-bold rounded-full"
            >
              Book a Training Call <ArrowUpRight size={16} />
            </a>
            <SocialIconRow size={22} className="justify-center" />
          </div>
        </div>
      )}
    </>
  );
}
